import {putAll, takeLatestF} from '@fl/cmsch-fe-library';
import {SagaIterator} from 'redux-saga';
import {isFull, none, opt} from 'ts-opt';
import {call, put, select} from 'typed-redux-saga';

import {Api} from 'api/gen/Api';
import {tCommon} from 'app/translations';
import {showBeError} from 'utils/show-be-error';
import {
    convertTableDataParamsToDto,
    getRequestedTableColumnSettings,
    getRequestedTablePageParams,
    simpleTableSelector,
    tablesAction,
} from 'utils/tables';

import {ManagementOrganizationsPage} from '../../types/management-organizations';
import {adminAction, GetManagementOrganizationsTableDataAction} from '../action';

const tableId = 'managementOrganizations';
const blankPageModel: ManagementOrganizationsPage = {
    content: [],
    page: {
        number: 1,
        size: 1,
        totalElements: 1,
        totalPages: 1,
    },
};

// eslint-disable-next-line max-lines-per-function
function* execute(action: GetManagementOrganizationsTableDataAction): SagaIterator {
    const {tableDataRequest, organizationId} = action.payload;
    if (tableDataRequest.isEmpty) return yield* put(adminAction.getManagementOrganizationsTableDataSuccess(none));

    const tableState = yield* select(simpleTableSelector.tableState(tableId));
    const tableDataParams = getRequestedTablePageParams({
        tableId,
        tableDataRequest: tableDataRequest.orCrash('No tableDataRequest for management organizations table'),
        tableState,
        resetType: 'tableDataParams',
    });

    if (!organizationId) {
        yield* put(adminAction.getManagementOrganizationsTableDataSuccess(opt(blankPageModel)));
        return yield* put(tablesAction.tableDataFetchSuccess(tableId, tableDataParams));
    }
    yield* put(tablesAction.tableDataFetchStart(tableId));

    const columnSettings = getRequestedTableColumnSettings<'managementOrganizations'>(tableState);
    const {filters, params} = convertTableDataParamsToDto({tableDataParams, columnSettings});
    const correctedFilter = opt(filters)
        .mapIn(x => (x.tag === 'CONTAINS_STRING' && x.field === 'username') ? x.value : null)
        .filterIn(isFull)
        .headIn()
        .orNull();
    const correctedParams = {sort: opt(params.sort).orElse([]), usernameFilterText: correctedFilter};

    const response = yield* call(Api.getSubjectOrganizationManagementPage, correctedParams, {organizationId});

    if (response.isSuccess) {
        const {headers, pagedModel} = response.data;
        yield* put(adminAction.getManagementOrganizationsTableDataSuccess(opt(pagedModel)));
        yield* put(adminAction.setManagementOrganizationsExtraColumns(headers || []));
        yield* put(tablesAction.tableDataFetchSuccess(tableId, tableDataParams));
    } else {
        yield putAll(showBeError(response, tCommon('tableLoadErrorTitle')));
        yield* put(tablesAction.tableDataFetchFailure(tableId));
    }
}

/** Runs the admin saga that loads organization-management table data. */
export function* getManagementOrganizationsTableData(): SagaIterator {
    yield takeLatestF('admin/GET_MANAGEMENT_ORGANIZATIONS_TABLE_DATA', execute);
}
